import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Loader2, Scale, Printer, Download, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

const Stat = ({ label, value, tone = 'slate' }) => (
  <div className={`rounded-xl border p-3 ${tone === 'rose' ? 'border-rose-200 bg-rose-50' : tone === 'emerald' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-2xl font-extrabold ${tone === 'rose' ? 'text-rose-700' : tone === 'emerald' ? 'text-emerald-700' : 'text-[#0E2A47]'}`}>{value}</div>
  </div>
);

const FindingBlock = ({ title, rows, columns, empty }) => (
  <div className="rounded-xl border border-slate-200 bg-white" data-testid={`recon-block-${title.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
      <span className="text-sm font-bold text-[#0E2A47]">{title}</span>
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${rows.length ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'}`}>{rows.length}</span>
    </div>
    <div className="px-4 py-3">
      {rows.length === 0 ? <p className="flex items-center gap-1.5 text-xs text-emerald-600"><CheckCircle2 size={13} />{empty}</p>
        : <div className="overflow-auto">
          <table className="min-w-full text-xs">
            <thead><tr className="border-b text-left text-[10px] uppercase text-slate-400">{columns.map((c) => <th key={c.k} className="py-1.5 pr-3">{c.label}</th>)}</tr></thead>
            <tbody>{rows.slice(0, 100).map((r, i) => (
              <tr key={i} className="border-b border-slate-50">{columns.map((c) => <td key={c.k} className="py-1.5 pr-3 text-slate-700">{c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? '—')}</td>)}</tr>))}</tbody>
          </table>
          {rows.length > 100 && <p className="mt-2 text-[10px] text-slate-400">Showing first 100 of {rows.length}. Export for the full list.</p>}
        </div>}
    </div>
  </div>
);

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const IMPORT_STATUS_LABELS = { locked_historical: 'Finalized (locked)', pending_ga_approval: 'Pending GA approval' };
const importStatus = (v) => IMPORT_STATUS_LABELS[v] || (v ? String(v).replace(/_/g, ' ') : '—');

export default function ReconciliationView() {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [dl, setDl] = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try { const res = await axios.get(`${API}/reconciliation/2026`, auth(token)); setData(res.data); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not load reconciliation'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (format) => {
    setDl(format);
    try {
      const res = await axios.get(`${API}/reconciliation/2026/export?format=${format}`, { ...auth(token), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `reconciliation_2026.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (_) { /* ignore */ }
    setDl('');
  };

  if (loading) return <div className="flex items-center gap-2 py-16 text-slate-400"><Loader2 className="animate-spin" size={18} />Loading 2026 reconciliation…</div>;
  if (err) return <div className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-600" data-testid="recon-error">{err}</div>;

  const t = data.totals || {};
  const f = data.findings || {};
  return (
    <div className="space-y-5" data-testid="reconciliation-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-extrabold text-[#0E2A47]"><Scale size={20} className="text-[#1763B6]" />2026 Historical Reconciliation</h1>
          <p className="text-xs text-slate-500">Read-only review of personnel, assignments, compensation and payroll from {data.cutoff}. Finalized payroll is never altered.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-100" data-testid="recon-refresh"><RefreshCw size={13} />Refresh</button>
          <button onClick={() => download('pdf')} disabled={!!dl} className="inline-flex items-center gap-1 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1763B6] disabled:opacity-50" data-testid="recon-export-pdf">{dl === 'pdf' ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}PDF</button>
          <button onClick={() => download('xlsx')} disabled={!!dl} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-100 disabled:opacity-50" data-testid="recon-export-xlsx">{dl === 'xlsx' ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}Excel</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        <Stat label="Personnel" value={t.personnel ?? 0} />
        <Stat label="Assignments" value={t.assignments ?? 0} />
        <Stat label="Compensation" value={t.compensation_records ?? 0} />
        <Stat label="2026 Changes" value={t.historical_changes_since_2026 ?? 0} />
        <Stat label="Discrepancies" value={t.discrepancies ?? 0} tone={t.discrepancies ? 'rose' : 'emerald'} />
        <Stat label="Completeness" value={`${t.completeness_pct ?? 0}%`} tone={(t.completeness_pct ?? 0) >= 90 ? 'emerald' : 'rose'} />
      </div>

      {t.discrepancies > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="recon-summary-banner">
          <AlertTriangle size={15} />{t.discrepancies} item(s) need review. This is a diagnostic report — no records are modified.
        </div>
      )}

      <FindingBlock title="Unmatched Payroll Lines" rows={f.unmatched_payroll_lines || []}
        empty="All 2026 payroll lines are matched to personnel."
        columns={[{ k: 'payee_name', label: 'Payee' }, { k: 'account_number', label: 'Account' }, { k: 'gross', label: 'Gross', fmt: money }, { k: 'period', label: 'Period' }, { k: 'import_status', label: 'Import status', fmt: importStatus }]} />

      <FindingBlock title="Active Personnel Missing Compensation" rows={f.personnel_missing_compensation || []}
        empty="Every payroll-eligible, assigned person has a compensation record."
        columns={[{ k: 'name', label: 'Personnel' }, { k: 'personnel_number', label: '#' }, { k: 'classification', label: 'Classification' }]} />

      <FindingBlock title="Active Personnel Without Assignment" rows={f.active_personnel_without_assignment || []}
        empty="All payroll-eligible personnel have an active assignment."
        columns={[{ k: 'name', label: 'Personnel' }, { k: 'personnel_number', label: '#' }, { k: 'classification', label: 'Classification' }]} />

      <FindingBlock title="Assignments Without Personnel Link" rows={f.assignments_without_personnel_link || []}
        empty="All active assignments are linked to a personnel record."
        columns={[{ k: 'location', label: 'Location' }, { k: 'customer', label: 'Customer' }, { k: 'name', label: 'Name on record' }, { k: 'role', label: 'Role' }]} />

      <FindingBlock title="Compensation for Inactive Personnel" rows={f.compensation_for_inactive_personnel || []}
        empty="No compensation records reference inactive personnel."
        columns={[{ k: 'name', label: 'Personnel' }, { k: 'personnel_number', label: '#' }, { k: 'amount', label: 'Amount', fmt: money }, { k: 'frequency', label: 'Frequency' }]} />

      <FindingBlock title="Inactive Personnel With Active Assignment" rows={f.inactive_personnel_with_active_assignment || []}
        empty="No terminated personnel remain actively assigned."
        columns={[{ k: 'name', label: 'Personnel' }, { k: 'personnel_number', label: '#' }, { k: 'active_assignments', label: 'Active assignments' }]} />

      <p className="text-[10px] text-slate-400">Generated {String(data.generated_at || '').slice(0, 19).replace('T', ' ')} · PREVIEW — diagnostic only, awaiting Global Administrator review.</p>
    </div>
  );
}
